import React, { useContext, useEffect, useMemo, useState } from 'react';
import { LayoutContext } from '../../contexts/layout.context';
import { useDispatch, useSelector } from 'react-redux';
import { authSelectors } from '../../store/selectors/auth';
import { getToken } from '../../utils/token';
import { testSelectors } from '../../store/selectors/test';
import { homeworkSelectors } from '../../store/selectors/homework';
import { useNavigate } from 'react-router-dom';
import { getUserInfoThunk } from '../../store/thunks/profile';
import { getHomeworkThunk } from '../../store/thunks/homework';
import actions from '../../store/actions';
import Head from '../../components/Head/Head';
import Spinner from '../../common/Spinner/Spinner';
import AttemptsContent from '../../components/Attempts/AttemptsContent';
import { attemptsSelectors } from '../../store/selectors/attempts';
import { continueTestThunk } from '../../store/thunks/test';
import sprite from '../../img/sprites.svg';
import { findNextHomeworkBlock } from '../../utils/homeworkNext';

import './test-finish.scss';

const readStored = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (e) {
    return null;
  }
};

const TestResult = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { setLayout } = useContext(LayoutContext);
  const auth = useSelector(authSelectors);
  const token = auth.token;
  const test = useSelector(testSelectors);
  const attempts = useSelector(attemptsSelectors);
  const homework = useSelector(homeworkSelectors);
  const finished = test.finished;
  const attemptsBlock = finished?.el_homework_tasks;
  const load = attempts.load;
  const currentTestId = readStored('currentTestId');
  const currentBlockId = readStored('homeworkId');

  const [showAnswers, setShowAnswers] = useState(false);
  const [requested, setRequested] = useState(false);

  getToken(token);

  useEffect(() => {
    document.title = 'Тест | New Brain';
    setLayout({ page: 'Тест' });
  }, [setLayout]);

  useEffect(() => {
    if (token) {
      dispatch(getUserInfoThunk({ token }));
    }
  }, [token]);

  useEffect(() => {
    if (currentTestId && token && !finished?.is_finished) {
      const data = { id: currentTestId, token };
      dispatch(continueTestThunk(data));
      setRequested(true);
    }
  }, [token]);

  useEffect(() => {
    if (!currentTestId && !finished?.is_finished) {
      navigate('/homework');
    }
  }, []);

  useEffect(() => {
    const eventId = readStored('eventId');
    const realHomeworkId = readStored('realHomeworkId');

    if (token && (eventId || realHomeworkId)) {
      dispatch(getHomeworkThunk({ event_id: eventId, homework_id: realHomeworkId, token }));
    }
  }, [token]);

  const nextBlock = useMemo(
    () => (homework.load ? null : findNextHomeworkBlock(homework.items, currentBlockId, currentTestId)),
    [homework.items, homework.load, currentBlockId, currentTestId]
  );

  const resetTestState = () => {
    dispatch(actions.test.getTest({}));
    dispatch(actions.test.getTestType(''));
    dispatch(actions.test.getTestAnswers([]));
    dispatch(actions.test.finishedTest({}));
    dispatch(actions.test.getTestError(''));
    dispatch(actions.test.checkTest({}));
    localStorage.removeItem('answersArr');
    localStorage.removeItem('definitionArr');
  };

  const goBack = () => {
    localStorage.removeItem('homeworkElId');
    localStorage.removeItem('homeworkId');
    localStorage.removeItem('homeworkBlockId');
    localStorage.removeItem('currentTestId');
    resetTestState();
    navigate('/homework');
  };

  const goNext = () => {
    if (!nextBlock) {
      goBack();

      return;
    }

    resetTestState();
    localStorage.setItem('homeworkElId', JSON.stringify(homework.info?.el_homework_id ?? readStored('homeworkElId')));
    localStorage.setItem('homeworkId', JSON.stringify(nextBlock.id));
    sessionStorage.setItem('openAccordion', String(nextBlock.id));

    localStorage.setItem('startTest', JSON.stringify(true));
    localStorage.removeItem('currentTestId');

    navigate('/homework/test');
  };

  const total = Number(finished?.total_questions) || 0;
  const correct = Number(finished?.correct_answered_questions) || 0;
  const percent = total > 0 ? Math.round((correct / total) * 100) : 0;
  const isReady = Boolean(finished?.is_finished);
  const isReviewRound = Boolean(finished?.is_review);
  const isPerfect = total > 0 && correct >= total;
  const mistakesFixed = Number(finished?.mistakes_fixed) || 0;
  const hasAnswers = Array.isArray(attemptsBlock) && attemptsBlock.length > 0;

  let finishTitle = isPerfect ? 'Жодної помилки! Молодець!' : 'Тест завершено!';
  let finishText = 'Кожен тест допомагає краще запам’ятовувати слова. Перегляньте відповіді, щоб закріпити знання.';

  if (isReviewRound) {
    finishTitle = 'Роботу над помилками зараховано!';
    finishText = 'Ви правильно відповіли на всі питання, де раніше були помилки. Так тримати!';
  } else if (mistakesFixed > 0) {
    finishText = `Помилки виправлено: ${mistakesFixed}. Оцінка — за першу спробу. Ці питання ще раз будуть у «Роботі над помилками» в кінці домашнього завдання.`;
  }

  return (
    <div className='test test--result container'>
      <Head goBack={goBack} />

      <div className="test__container test__container--lg">
        {!isReady && requested && !test.load && (
          <section className="test-finish">
            <p className="test-finish__text">Не вдалося показати результат. Поверніться до списку завдань.</p>
            <button type="button" className="test-finish__btn" onClick={goBack}>До домашнього завдання</button>
          </section>
        )}

        {!isReady ? (!requested || test.load) && <Spinner /> : (
          <section className="test-finish">
            <div className="test-finish__head">
              <span className="test-finish__icon">
                <svg aria-hidden="true"><use href={`${sprite}#check-circle`}></use></svg>
              </span>
              <div className="test-finish__names">
                <h3 className="test-finish__title">{finishTitle}</h3>
              </div>
            </div>

            {!isReviewRound && (
              <div className="test-finish__result">
                <span className="test-finish__label">Ваш результат</span>
                <div className="test-finish__score">
                  <b>{correct} із {total}</b>
                  <span>{percent}%</span>
                </div>
                <span className="test-finish__bar"><i style={{ width: `${percent}%` }}></i></span>
              </div>
            )}

            <p className="test-finish__text">{finishText}</p>

            <div className="test-finish__actions">
              <button
                type="button"
                className="test-finish__btn"
                disabled={homework.load}
                onClick={goNext}
              >
                {homework.load ? 'Зачекайте…' : nextBlock ? 'Перейти до наступного завдання →' : 'До домашнього завдання'}
              </button>
              {hasAnswers && (
                <button
                  type="button"
                  className="test-finish__btn test-finish__btn--light"
                  aria-expanded={showAnswers}
                  onClick={() => setShowAnswers((value) => !value)}
                >
                  {showAnswers ? 'Сховати відповіді' : 'Переглянути відповіді'}
                </button>
              )}
            </div>
          </section>
        )}

        {isReady && hasAnswers && showAnswers && (
          <div className="test-finish__answers">
            {load ? <Spinner /> : <AttemptsContent attemptsInfo={finished} attemptsBlock={attemptsBlock} />}
          </div>
        )}
      </div>
    </div>
  );
};

export default TestResult;
